(() => {
  const pending = new WeakSet();
  const initialized = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const conserveData = () => connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
  // Films start after the page's own resources so they never compete with them; cap the wait for slow third parties.
  const pageLoaded = document.readyState === 'complete' ? Promise.resolve() : new Promise(resolve => {
    window.addEventListener('load', resolve, { once: true });
    setTimeout(resolve, 6000);
  });
  const visible = (video) => {
    const rect = video.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  };
  const manual = (video) => video.hasAttribute('data-suri-video-manual') ||
    reducedMotion.matches || conserveData();
  const prepare = async (video) => {
    const requested = video.dataset.suriVideoRequested === 'true';
    if (pending.has(video) || (manual(video) && !requested) || !visible(video) || document.hidden) return;
    if (video.dataset.suriVideoReady === 'true') {
      try { await video.play(); } catch (_) { /* The poster stays when autoplay is refused. */ }
      return;
    }
    pending.add(video);
    const image = video.parentElement.querySelector('picture img');
    try {
      if (image) await image.decode();
      else if (video.poster) { const poster = new Image(); poster.src = video.poster; await poster.decode(); }
    } catch (_) { /* A failed poster must not disable playback. */ }
    await pageLoaded;
    await new Promise(resolve => requestAnimationFrame(resolve));
    if (!video.isConnected || !visible(video) || document.hidden || (manual(video) && !requested)) {
      pending.delete(video);
      return;
    }
    // Select a single source explicitly: mobile source media is not supported by every browser.
    const source = Array.from(video.querySelectorAll('source[data-src]')).find(item =>
      !item.media || window.matchMedia(item.media).matches);
    if (!source) { pending.delete(video); return; }
    video.src = source.dataset.src;
    video.dataset.suriVideoReady = 'true';
    video.load();
    if (!video.hasAttribute('data-hero-slide') || video.classList.contains('is-active')) {
      try { await video.play(); } catch (_) { /* The poster stays when autoplay is refused. */ }
    }
    pending.delete(video);
  };
  const schedule = (video) => {
    if (manual(video)) return;
    prepare(video);
  };
  const scheduleAll = () => document.querySelectorAll('video[data-suri-video]').forEach(schedule);
  document.addEventListener('suri:video-request', event => {
    if (event.target.matches('video[data-suri-video]')) prepare(event.target);
  });
  const initialize = () => {
    document.querySelectorAll('video[data-suri-video]').forEach(video => {
      if (initialized.has(video)) return;
      initialized.add(video);
      video.addEventListener('playing', () => { video.dataset.suriVideoPlaying = 'true'; });
      video.addEventListener('error', () => { video.dataset.suriVideoReady = 'false'; });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) schedule(video);
          else if (!video.paused) video.pause();
        }));
        observer.observe(video);
      } else schedule(video);
    });
  };
  // There is no play control, so resume automatically once the page is visible again.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('video[data-suri-video]').forEach(video => video.pause());
    else scheduleAll();
  });
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) document.querySelectorAll('video[data-suri-video]').forEach(video => video.pause());
    else scheduleAll();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
